import { webLightTheme } from '@fluentui/react-components';

// Brand colors from Figma tokens (Brand/Brand 1)
const brandColors = {
  "10": "#050300",
  "20": "#1f1707", 
  "30": "#33250c",
  "40": "#43300d",
  "50": "#533b0d",
  "60": "#64460c",
  "70": "#755209", // Link color
  "80": "#875e05", // Primary brand
  "90": "#996b02",
  "100": "#a57927",
  "110": "#b28842",
  "120": "#be985b",
  "130": "#c9a774", // Logo border, main gold
  "140": "#d4b78d",
  "150": "#dfc7a6",
  "160": "#e9d8c0",
};

// Neutral colors from Figma Global/Value tokens
const neutralColors = {
  white: "#ffffff",
  black: "#000000",
  grey: {
    "2": "#050505",
    "4": "#0a0a0a", 
    "6": "#0f0f0f",
    "8": "#141414",
    "10": "#1a1a1a",
    "12": "#1f1f1f",
    "14": "#242424", // Primary text
    "16": "#292929",
    "18": "#2e2e2e",
    "20": "#333333",
    "22": "#383838",
    "24": "#3d3d3d", // Footer border
    "26": "#424242", // Secondary text
    "28": "#474747",
    "30": "#4d4d4d",
    "32": "#525252",
    "34": "#575757",
    "36": "#5c5c5c",
    "38": "#616161", // Tertiary text
    "40": "#666666",
    "42": "#6b6b6b",
    "44": "#707070", // Placeholder text
    "46": "#757575",
    "48": "#7a7a7a",
    "50": "#808080",
    "52": "#858585",
    "54": "#8a8a8a",
    "56": "#8f8f8f",
    "58": "#949494",
    "60": "#999999",
    "62": "#9e9e9e",
    "64": "#a3a3a3",
    "66": "#a8a8a8",
    "68": "#adadad",
    "70": "#b3b3b3",
    "72": "#b8b8b8",
    "74": "#bdbdbd", // Disabled text
    "76": "#c2c2c2",
    "78": "#c7c7c7",
    "80": "#cccccc",
    "82": "#d1d1d1", // Primary stroke
    "84": "#d6d6d6",
    "86": "#dbdbdb",
    "88": "#e0e0e0", // Secondary stroke
    "90": "#e6e6e6", // Footer background
    "92": "#ebebeb",
    "94": "#f0f0f0", // Accordion hover/open
    "96": "#f5f5f5",
    "98": "#fafafa", // Light background
  },
  // Custom page backgrounds
  pageBackground: "#f5f0e6", // KitRegistration background
  contentBackground: "#f5f5f7", // Main content background
};

// Status colors from Figma Shared colors
const statusColors = {
  danger: {
    primary: "#c50f1f",
    background1: "#fdf3f4",
    background3: "#c50f1f",
  },
  warning: {
    primary: "#ff8c00",
    background1: "#fff9f5",
    background3: "#ff8c00",
  },
  success: {
    primary: "#107c10",
    background1: "#f1faf1",
    background3: "#107c10",
  }
};

// Shadow tokens from Figma
const shadowTokens = {
  ambient: "#0000001f",
  ambientLighter: "#0000000f", 
  ambientDarker: "#00000033",
  key: "#00000024",
  keyLighter: "#00000012",
  keyDarker: "#0000003d",
};

// ============================================================================
// LEGACY: FluentUI Theme (V1 Routes Only)
// ============================================================================
// TODO: Remove this section when V1 routes are deleted
// This FluentUI theme is ONLY for legacy /v1/* routes
// New feature pages use Tailwind CSS + tokens below
// ============================================================================

// Create theme using Figma token structure
const theme = {
  ...webLightTheme,
  
  // Export brand colors for direct access
  brandColors,
  
  // Brand colors
  colorBrandBackground: brandColors["80"], // #875e05
  colorBrandBackgroundHover: brandColors["70"],
  colorBrandBackgroundPressed: brandColors["60"],
  colorBrandBackgroundSelected: brandColors["70"],
  
  colorBrandForeground1: brandColors["80"],
  colorBrandForeground2: brandColors["70"],
  
  // Link colors  
  colorBrandForegroundLink: brandColors["70"], // #755209
  colorBrandForegroundLinkHover: brandColors["60"],
  colorBrandForegroundLinkPressed: brandColors["50"],
  colorBrandForegroundLinkSelected: brandColors["70"],
  
  // Compound brand (for checked states)
  colorCompoundBrandBackground: brandColors["80"],
  colorCompoundBrandBackgroundHover: brandColors["70"],
  colorCompoundBrandBackgroundPressed: brandColors["80"],
  
  colorCompoundBrandForeground1: brandColors["80"],
  colorCompoundBrandForeground1Hover: brandColors["70"],
  colorCompoundBrandForeground1Pressed: brandColors["80"],
  
  colorCompoundBrandStroke: brandColors["80"],
  colorCompoundBrandStrokeHover: brandColors["70"],
  colorCompoundBrandStrokePressed: brandColors["80"],
  
  // Neutral backgrounds
  colorNeutralBackground1: neutralColors.white,
  colorNeutralBackground2: neutralColors.grey["98"], // #fafafa
  colorNeutralBackground3: neutralColors.grey["94"], // #f0f0f0 - accordion hover
  colorNeutralBackground4: neutralColors.grey["96"], // #f5f5f5
  colorNeutralBackground5: neutralColors.grey["20"], // Hover grey
  colorNeutralBackground6: neutralColors.pageBackground, // #f5f0e6
  colorNeutralBackground7: neutralColors.contentBackground, // #f5f5f7
  
  // Header background (dark)
  colorNeutralBackground1Pressed: neutralColors.grey["14"], // #242424
  
  colorNeutralBackgroundDisabled: neutralColors.grey["94"],
  
  // Text colors
  colorNeutralForeground1: neutralColors.grey["14"], // #242424
  colorNeutralForeground2: neutralColors.grey["26"], // #424242  
  colorNeutralForeground3: neutralColors.grey["38"], // #616161
  colorNeutralForeground4: neutralColors.grey["44"], // #707070
  
  colorNeutralForegroundInverted: neutralColors.white,
  colorNeutralForegroundOnBrand: neutralColors.white,
  colorNeutralForegroundDisabled: neutralColors.grey["74"],
  
  // Stroke colors
  colorNeutralStroke1: neutralColors.grey["82"], // #d1d1d1
  colorNeutralStroke2: neutralColors.grey["88"], // #e0e0e0
  colorNeutralStroke3: neutralColors.grey["94"], // #f0f0f0
  
  colorNeutralStrokeAccessible: neutralColors.grey["38"],
  colorNeutralStrokeAccessibleHover: neutralColors.grey["34"],
  colorNeutralStrokeAccessiblePressed: neutralColors.grey["30"],
  colorNeutralStrokeAccessibleSelected: brandColors["80"],
  
  colorNeutralStrokeDisabled: neutralColors.grey["88"],
  
  // Status colors
  colorPaletteRedBackground3: statusColors.danger.background3,
  colorPaletteRedForeground1: statusColors.danger.primary,
  
  // Button colors - consistent across the app
  colorButtonPrimary: neutralColors.grey["14"], // #242424
  colorButtonPrimaryHover: neutralColors.grey["20"], // #333333
  colorButtonPrimaryPressed: neutralColors.grey["16"], // #292929
  colorButtonSecondary: neutralColors.white,
  colorButtonSecondaryHover: neutralColors.grey["98"], // #fafafa
  colorButtonSecondaryPressed: neutralColors.grey["96"], // #f5f5f5
  
  // Shadow system
  shadow4: `0 1.6px 3.6px 0 ${shadowTokens.ambient}, 0 0.3px 0.9px 0 ${shadowTokens.key}`,
  shadow8: `0 3.2px 7.2px 0 ${shadowTokens.ambient}, 0 0.6px 1.8px 0 ${shadowTokens.key}`,
  shadow16: `0 6.4px 14.4px 0 ${shadowTokens.ambient}, 0 1.2px 3.6px 0 ${shadowTokens.key}`,
  shadow28: `0 11.2px 22.4px 0 ${shadowTokens.ambient}, 0 2.4px 6.4px 0 ${shadowTokens.key}`,
  shadow64: `0 25.6px 51.2px 0 ${shadowTokens.ambient}, 0 6.4px 19.2px 0 ${shadowTokens.keyDarker}`,
  
  // Typography
  fontFamilyBase: '"Figtree", "Segoe UI", system-ui, Avenir, Helvetica, Arial, sans-serif',
  
  // Font sizes from Figma
  fontSizeBase100: '10px',
  fontSizeBase200: '12px', 
  fontSizeBase300: '14px',
  fontSizeBase400: '16px',
  fontSizeBase500: '20px',
  fontSizeBase600: '24px',
  fontSizeHero700: '28px',
  fontSizeHero800: '32px',
  fontSizeHero900: '40px',
  fontSizeHero1000: '68px',
  
  // Line heights from Figma
  lineHeightBase100: '14px',
  lineHeightBase200: '16px',
  lineHeightBase300: '20px', 
  lineHeightBase400: '22px',
  lineHeightBase500: '28px',
  lineHeightBase600: '32px',
  lineHeightHero700: '36px',
  lineHeightHero800: '40px',
  lineHeightHero900: '52px',
  lineHeightHero1000: '92px',
  
  // Border radius from Figma
  borderRadiusNone: '0px',
  borderRadiusSmall: '2px',
  borderRadiusMedium: '4px', 
  borderRadiusLarge: '6px',
  borderRadiusXLarge: '8px',
  borderRadiusCircular: '9999px',
};

export default theme;

// ============================================================================
// NEW: Token Exports for Tailwind CSS
// ============================================================================
// These tokens are imported by tailwind.config.js for new feature pages
// Single source of truth for all design tokens
// ============================================================================

export { brandColors, neutralColors, statusColors, shadowTokens };
